/* I numeri, le date e le misure come si scrivono qui (§ 12).
 *
 * **Perche' un modulo e non `toFixed` sul posto.** Su una pagina si vede un
 * numero; su novecento righe si vede una colonna, e una colonna dove il
 * separatore cambia da una riga all'altra non si confronta piu' — si rilegge.
 * Il formato sbagliato, poi, e' l'errore piu' silenzioso che ci sia: `2.84`
 * non da' nessun segnale a chi legge in italiano, dice solo un'altra cosa.
 *
 * **Lo spazio fra le migliaia e' sottile, non un punto.** L'italiano scrive
 * `12.345`, ma qui accanto stanno quote e distanze con la virgola decimale, e
 * un punto in mezzo a una colonna di misure e' esattamente il segno che
 * qualcuno leggera' come decimale. Il codice di disegno lo risolve cosi': la
 * virgola separa i decimali, uno spazio sottile separa le migliaia.
 *
 * Qui sta solo cio' che qualche schermo scrive davvero. Le coordinate, la
 * tensione e il margine sul franco hanno la loro regola nel codice di disegno,
 * e arriveranno insieme allo schermo che li mostra: una funzione scritta prima
 * del suo lettore e' una funzione che nessuno prova.
 */
import { currentLocale, setCurrentLocale, type Locale } from './i18n/locale'

/** Il formato segue la lingua dell'interfaccia, non quella del navigatore. */
export function setFormatLocale(next: Locale): void {
  setCurrentLocale(next)
}

function dateLocale(): string {
  return currentLocale() === 'en' ? 'en-GB' : 'it-IT'
}

// Spazio stretto insecabile: separa le migliaia senza mandare a capo il numero.
const THIN = '\u202f'

/** Un numero come si legge in italiano: virgola e migliaia sottili. */
export function decimal(value: number, digits = 0): string {
  const written = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    // Sempre, anche a quattro cifre. La regola italiana ne farebbe a meno —
    // `2412` senza separatore — ma in una colonna accanto a `12 345` le due
    // righe smettono di confrontarsi a colpo d'occhio, ed e' il confronto il
    // lavoro di chi guarda. Visto sulla schermata delle tappe.
    useGrouping: 'always',
  }).format(value)
  return written.replace(/\./g, THIN)
}

/** Una misura in metri: sempre due decimali, perche' `2,8` e `2,84` in colonna
 * non si confrontano. */
export function metres(value: number): string {
  return `${decimal(value, 2)}${THIN}m`
}

/** Il margine sul franco. Il segno c'e' sempre, anche quando e' positivo:
 * senza, `0,34` e `-0,34` si distinguono per un trattino che si perde, e sono
 * due mondi diversi — dentro norma e fuori norma. */
export function margin(value: number | null): string {
  if (value === null) return notMeasured()
  const sign = value < 0 ? '\u2212' : '+'
  return `${sign}${decimal(Math.abs(value), 2)}${THIN}m`
}

/** «Non misurabile»: un buco nella nuvola non e' una conformita' accertata, e
 * non si scrive come uno zero. */
export function notMeasured(): string {
  return currentLocale() === 'en' ? 'n/a' : 'n/d'
}


/** Una quota in percento, arrotondata: i decimali di una percentuale di
 * avanzamento non li guarda nessuno. */
export function share(fraction: number): string {
  return `${decimal(Math.round(fraction * 100))}${THIN}%`
}

/** Il peso di un pacchetto, in unita' decimali: i dischi si vendono cosi', e
 * chi carica confronta con quello che c'e' scritto sul disco. */
export function size(bytes: number): string {
  const units = ['B', 'kB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000
    unit += 1
  }
  const digits = value >= 100 || unit === 0 ? 0 : 1
  return `${decimal(value, digits)}${THIN}${units[unit]}`
}

/** Una data come si legge qui: giorno, mese, anno. */
export function day(moment: Date | string): string {
  const when = typeof moment === 'string' ? new Date(moment) : moment
  if (Number.isNaN(when.getTime())) return notMeasured()
  return new Intl.DateTimeFormat(dateLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(when)
}

/** Data e ora, per il giornale e per le ricevute. */
export function moment(when: Date | string): string {
  const at = typeof when === 'string' ? new Date(when) : when
  if (Number.isNaN(at.getTime())) return notMeasured()
  return `${day(at)} ${new Intl.DateTimeFormat(dateLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(at)}`
}

/** «3 minuti fa», «ieri», «2 giorni fa»: quanto e' passato, a parole, nella
 *  lingua della scheda. Oltre un mese si torna alla data: «47 giorni fa» si
 *  legge peggio di «23/07/2026». */
export function ago(when: Date | string, now: Date = new Date()): string {
  const then = typeof when === 'string' ? new Date(when) : when
  if (Number.isNaN(then.getTime())) return notMeasured()
  const seconds = Math.round((then.getTime() - now.getTime()) / 1000)
  const past = Math.abs(seconds)
  if (past > 30 * 86400) return day(then)
  const relative = new Intl.RelativeTimeFormat(currentLocale(), { numeric: 'auto' })
  if (past < 60) return relative.format(0, 'minute').replace(/^in 0 minutes$|^tra 0 minuti$/, '')
  if (past < 3600) return relative.format(Math.round(seconds / 60), 'minute')
  if (past < 86400) return relative.format(Math.round(seconds / 3600), 'hour')
  return relative.format(Math.round(seconds / 86400), 'day')
}
